import { useState } from "react";
import {
  checklistContentKey,
  readChecklistProgress,
  type MaterialChecklist,
} from "cadendar-shared";
import { trpc } from "../../main/trpc";

function ChecklistPreparation({
  checklist,
  storageKey,
}: {
  checklist: MaterialChecklist;
  storageKey: string;
}) {
  const [state, setState] = useState(() => {
    try {
      return {
        ...readChecklistProgress(localStorage.getItem(storageKey), checklist),
        storageError: false,
      };
    } catch {
      return { ...readChecklistProgress(null, checklist), storageError: true };
    }
  });
  const checked = new Set(state.progress.checkedIds);
  const completed =
    checklist.items.length > 0 && checked.size === checklist.items.length;
  const persist = (checkedIds: string[]) => {
    const progress = { contentKey: checklistContentKey(checklist), checkedIds };
    let storageError = false;
    try {
      localStorage.setItem(storageKey, JSON.stringify(progress));
    } catch {
      storageError = true;
    }
    setState({ progress, needsRestart: false, storageError });
  };
  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-xl font-semibold">{checklist.name}</h2>
        <button
          type="button"
          className="min-h-11 rounded border px-3 py-2"
          onClick={() => {
            if (
              window.confirm(
                "Recommencer cette préparation et décocher tous les éléments ?",
              )
            )
              persist([]);
          }}
        >
          Recommencer
        </button>
      </div>
      {state.storageError && (
        <p role="alert" className="rounded bg-amber-50 p-3 text-amber-900">
          Le stockage du téléphone est indisponible. Le cochage fonctionne, mais
          ne sera pas conservé après fermeture.
        </p>
      )}
      {state.needsRestart && (
        <div
          role="alert"
          className="space-y-2 rounded bg-amber-50 p-3 text-amber-900"
        >
          <p>
            Le contenu de cette checklist a changé. Recommencez la préparation
            pour vérifier le matériel à partir du nouveau modèle.
          </p>
          <button
            type="button"
            className="min-h-11 rounded border border-amber-700 px-3 py-2"
            onClick={() => persist([])}
          >
            Commencer la nouvelle préparation
          </button>
        </div>
      )}
      <p
        role="status"
        className={
          completed ? "font-semibold text-green-700" : "text-slate-600"
        }
      >
        {checked.size} / {checklist.items.length} élément
        {checklist.items.length > 1 ? "s" : ""} sorti
        {checklist.items.length > 1 ? "s" : ""}
        {completed ? " — Tout est prêt !" : ""}
      </p>
      {checklist.items.length > 0 && (
        <progress
          aria-label="Progression de la préparation"
          className="h-3 w-full accent-green-600"
          max={checklist.items.length}
          value={checked.size}
        />
      )}
      {!checklist.items.length && (
        <p>Cette checklist ne contient pas encore de matériel.</p>
      )}
      <ul className="space-y-2">
        {checklist.items.map((item) => (
          <li key={item.id}>
            <label
              className={`flex min-h-16 cursor-pointer items-center gap-4 rounded-lg border p-4 ${checked.has(item.id) ? "border-green-200 bg-green-50" : "border-slate-200 bg-white"} ${state.needsRestart ? "opacity-50" : ""}`}
            >
              <input
                type="checkbox"
                className="h-7 w-7 shrink-0 accent-green-600"
                checked={checked.has(item.id)}
                disabled={state.needsRestart}
                onChange={(event) =>
                  persist(
                    event.target.checked
                      ? [...checked, item.id]
                      : [...checked].filter((id) => id !== item.id),
                  )
                }
              />
              <span
                className={
                  checked.has(item.id) ? "text-green-900" : "text-slate-900"
                }
              >
                {item.label}
                {item.catalogReference && (
                  <small className="mt-1 block text-slate-500">
                    Réf. {item.catalogReference}
                  </small>
                )}
              </span>
            </label>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function ChecklistsScreen({ userId }: { userId: string }) {
  const query = trpc.checklist.list.useQuery();
  const [selectedId, setSelectedId] = useState("");
  const selected = query.data?.checklists.find(
    (checklist) => checklist.id === selectedId,
  );
  return (
    <main className="mx-auto max-w-2xl space-y-5 p-4">
      <h1 className="text-2xl font-semibold">Préparer le matériel</h1>
      <p className="text-sm text-slate-600">
        Le cochage est conservé uniquement sur ce téléphone.
      </p>
      {query.isPending && <p role="status">Chargement des checklists…</p>}
      {query.error && (
        <div role="alert" className="space-y-2 text-red-700">
          <p>
            Impossible d’actualiser les checklists. Vérifiez votre connexion.
          </p>
          <button
            className="min-h-11 rounded border px-3 py-2"
            onClick={() => void query.refetch()}
          >
            Réessayer
          </button>
        </div>
      )}
      {query.data && (
        <>
          {!query.data.checklists.length ? (
            <p>
              Aucune checklist définie. Créez-en une dans Cadendar, menu «
              Checklists de matériel ».
            </p>
          ) : (
            <label className="block font-medium">
              Checklist à préparer
              <select
                className="mt-2 min-h-12 w-full rounded border border-slate-300 bg-white p-3"
                value={selected?.id ?? ""}
                onChange={(event) => setSelectedId(event.target.value)}
              >
                <option value="">Choisir une checklist</option>
                {query.data.checklists.map((checklist) => (
                  <option key={checklist.id} value={checklist.id}>
                    {checklist.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          {selected && (
            <ChecklistPreparation
              key={`${query.data.organizationId}:${userId}:${selected.id}:${checklistContentKey(selected)}`}
              checklist={selected}
              storageKey={`cadendar:checklist:${query.data.organizationId}:${userId}:${selected.id}`}
            />
          )}
        </>
      )}
    </main>
  );
}
